import {
  EVIDENCE_TYPES,
  type ApplicationPhase,
  type DocumentReview,
  type EvidenceDocumentStatus,
  type EvidenceType,
} from '@paguz/contracts';

/** Lenguaje claro de la originación (fases, documentos, revisión). */

/** Etapas que ve el cliente (agrupan las 11 fases del expediente). */
export const CUSTOMER_STAGES: readonly {
  id: string;
  label: string;
  description: string;
  phases: readonly ApplicationPhase[];
}[] = [
  {
    id: 'start',
    label: 'Conversemos',
    description: 'Tu negocio, lo que necesitas y tus autorizaciones',
    // IDENTITY: la consulta en listas y centrales corre sola al aceptar las autorizaciones.
    phases: ['DISCOVERY', 'CONSENT', 'IDENTITY'],
  },
  {
    id: 'profile',
    label: 'Ingresos y gastos',
    description: 'Ventas, gastos y personas a cargo',
    phases: ['BUSINESS_PROFILE'],
  },
  {
    id: 'evidence',
    label: 'Documentos',
    description: 'Soportes de tus ventas',
    phases: ['EVIDENCE'],
  },
  {
    id: 'cashflow',
    label: 'Flujo de caja y cuotas',
    description: 'Tu capacidad de pago y la simulación',
    phases: ['CASH_FLOW', 'OFFER_SIMULATION'],
  },
  {
    id: 'decision',
    label: 'Decisión del banco',
    description: 'Revisión de tu solicitud',
    phases: ['SUBMITTED', 'DECISION'],
  },
  {
    id: 'signature',
    label: 'Firma',
    description: 'Aceptas la oferta y firmas',
    phases: ['FORMALIZATION'],
  },
  { id: 'active', label: 'Cupo activo', description: '¡A comprar!', phases: ['ACTIVE'] },
];

export function stageIndex(phase: ApplicationPhase): number {
  const index = CUSTOMER_STAGES.findIndex((stage) => stage.phases.includes(phase));
  return index === -1 ? 0 : index;
}

export const PHASE_LABEL: Readonly<Record<ApplicationPhase, string>> = {
  DISCOVERY: 'Conociendo tu negocio',
  CONSENT: 'Autorizaciones',
  IDENTITY: 'Consulta en centrales',
  BUSINESS_PROFILE: 'Ingresos y gastos del negocio y del hogar',
  EVIDENCE: 'Documentos',
  CASH_FLOW: 'Flujo de caja',
  OFFER_SIMULATION: 'Simulación del cupo',
  SUBMITTED: 'Solicitud enviada',
  DECISION: 'Decisión del banco',
  FORMALIZATION: 'Firma del pagaré',
  ACTIVE: 'Cupo activo',
};

export function evidenceLabel(type: EvidenceType | null): string {
  return type === null ? 'Documento' : EVIDENCE_TYPES.labels[type];
}

export const DOCUMENT_STATUS: Readonly<
  Record<
    EvidenceDocumentStatus,
    { label: string; tone: 'secondary' | 'success' | 'warning' | 'danger' | 'outline' }
  >
> = {
  UPLOADED: { label: 'Recibido', tone: 'secondary' },
  ANALYZING: { label: 'En revisión', tone: 'secondary' },
  ACCEPTED: { label: 'Aceptado', tone: 'success' },
  NEEDS_REPLACEMENT: { label: 'Por reemplazar', tone: 'warning' },
  REJECTED: { label: 'No válido', tone: 'danger' },
  SUPERSEDED: { label: 'Reemplazado', tone: 'outline' },
};

export const LEGIBILITY: Readonly<Record<DocumentReview['legibility'], string>> = {
  GOOD: 'Se lee bien',
  FAIR: 'Se lee con dificultad',
  POOR: 'No se lee bien',
};

export const VALIDITY: Readonly<Record<DocumentReview['validity'], string>> = {
  VALID: 'Vigente',
  EXPIRED: 'Vencido',
  UNKNOWN: 'Sin fecha clara',
};

export const VERDICT: Readonly<
  Record<DocumentReview['verdict'], { label: string; tone: 'success' | 'warning' | 'danger' }>
> = {
  ACCEPTED: { label: 'Aceptado', tone: 'success' },
  NEEDS_REPLACEMENT: { label: 'Hay que repetirlo', tone: 'warning' },
  REJECTED: { label: 'No sirve como soporte', tone: 'danger' },
};

/** Tipos que el cliente puede elegir al subir un documento desde "Mi solicitud". */
export const UPLOADABLE_TYPES: readonly EvidenceType[] = [
  'ID_FRONT',
  'ID_BACK',
  'SALES_NOTEBOOK',
  'SUPPLIER_INVOICE',
  'BANK_STATEMENT',
  'WALLET_STATEMENT',
  'BUSINESS_RECORDS',
  'UTILITY_BILL',
  'BUSINESS_PHOTO',
  'INVENTORY_PHOTO',
  'OTHER',
];

export const CASH_FLOW_SECTION: Readonly<Record<string, string>> = {
  SALES: 'Ventas',
  COGS: 'Costo de lo que vendes',
  OPEX: 'Gastos del negocio',
  OTHER_INCOME: 'Otros ingresos',
  HOUSEHOLD: 'Gastos del hogar',
  DEBT: 'Otras deudas',
  ASSET: 'Bienes',
  LIABILITY: 'Deudas',
};

export const CASH_FLOW_METHOD: Readonly<Record<string, string>> = {
  DECLARED: 'Lo que nos contaste',
  DOCUMENT: 'Tomado de tus documentos',
  TRIANGULATED: 'Comparado entre varias fuentes',
  ESTIMATED: 'Estimado',
};
